import { Box, Spinner, Text } from '@zillow/constellation-10';

import { useFetchComparables } from '../../../../hooks/queries/graphql';
import { AppSelectors, RoutingSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';
import propertyUtils from '../../../../utils/propertyUtils';

import StaticMap from '../../../../../../src/components/StaticMap';

import { flexStyles } from '../../styles';

import CompsCTA from '../CompsCTA';

import type { LISTING_SUMMARY_DISPLAY_STATUS } from '../../../../constants/ListingSummaryConstants';

type Props = {
  status: keyof typeof LISTING_SUMMARY_DISPLAY_STATUS;
  hasAnalyticsRentComps?: boolean;
};

type Geo = {
  lat: string;
  lon: string;
};

type Comp = {
  active: boolean;
  geo: Geo;
};

const Comps = ({ status, hasAnalyticsRentComps = false }: Props) => {
  const listingAlias = useSelector((state) => RoutingSelectors.makeListingId(state));
  const isScreenTablet = useSelector((state) => AppSelectors.makeIsScreenTablet(state));
  const user = useSelector((state) => state.user);

  const { data = {}, isLoading, error } = useFetchComparables(listingAlias);

  if (isLoading) {
    return (
      <Box css={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
        <Spinner size="sm" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box css={{ overflow: 'auto', ...flexStyles }}>
        <Text textStyle="body-sm">{error.message}</Text>
      </Box>
    );
  }

  // no floorplans
  // @ts-expect-error
  if (!!data?.floorplans?.length === false) {
    return (
      <Box css={{ overflow: 'auto', ...flexStyles }}>
        <Text textStyle="body-sm">No information found for this listing.</Text>
      </Box>
    );
  }

  // @ts-expect-error
  const comparableFloorplans = (data?.hasOwnProperty('similarFloorplans') && data.similarFloorplans.floorplans) || [];

  if (comparableFloorplans.length === 0) {
    return (
      <Box css={flexStyles}>
        <Text textStyle="body-sm">We are unable to find any comparable listings nearby.</Text>
      </Box>
    );
  }

  const {
    // @ts-expect-error
    floorplans,
    // @ts-expect-error
    similarFloorplans: { floorplans: compFloorplans, highestPriceIncluded, lowestPriceIncluded },
  } = data;

  const { address = {} } = floorplans[0] ?? {};
  const { formattedCompleteAddress } = propertyUtils.formatAddress(address);
  const contentComps = `${compFloorplans.length} comparable properties`;
  const contentSummary = `listed nearby for $${lowestPriceIncluded} - $${highestPriceIncluded}`;

  const generateAdditionalMarkers = () => {
    const formattedMarkers = compFloorplans.map((comp: Comp) => {
      return `color:${comp.active ? 'purple' : 'gray'}|size:tiny|${comp.geo.lat},${comp.geo.lon}`;
    });
    return formattedMarkers.join('&markers=');
  };

  return (
    <Box css={{ flexGrow: 1 }} className="Comps">
      <Box css={{ display: 'grid', gap: '400', gridTemplateColumns: '50% 50%' }}>
        <Box
          css={{
            display: 'flex',
            justifyContent: 'center',
            alignContent: 'center',
            overflow: 'hidden',
            height: '125px',
            width: '100%',
            '& > img': {
              objectFit: 'cover',
            },
          }}
        >
          <StaticMap
            address={formattedCompleteAddress}
            additionalMarkers={generateAdditionalMarkers()}
            scale={2}
            size={isScreenTablet ? '350x125' : '180x125'}
            user={user}
          />
        </Box>
        <Box css={{ overflow: 'hidden', position: 'relative', height: '125px' }}>
          <Box css={{ textAlign: 'left', marginRight: '200' }}>
            <Text
              asChild
              css={{
                textStyle: { base: 'body-sm-bold', xl: 'body-bold' },
              }}
            >
              <p>{contentComps}</p>
            </Text>
            <Text asChild textStyle="body-sm">
              <p>{contentSummary}</p>
            </Text>
          </Box>
        </Box>
      </Box>

      <CompsCTA address={address} status={status} hasAnalyticsRentComps={hasAnalyticsRentComps} />
    </Box>
  );
};

export default Comps;
